import { useMemo } from 'react';
import { useQuery } from 'react-query';
import isEmpty from 'lodash/isEmpty';
import ResidentService from '../../services/residentService';
import HouseholdService from '../../services/householdService';

/**
 * Whether the household has an open certification (excluding Gross Rent),
 * via ResidentService.getHouseholdHasOpenCerts / GET .../has-open-certs.
 *
 * This is the same endpoint EditResidentHousehold's Redux saga already uses
 * to gate the legacy "Edit Household" page, and is the variant the
 * Mini-Profile's Start/End Date open-cert gate should use (see the
 * has-non-move-out-open-certs carve-out in useFetchHasNonMoveOutOpenCerts,
 * which is specific to ResidentMoveOut's "Death of Sole Family Member"
 * scenario and does not apply here).
 *
 * Extracted from ResidentProfile/ResidentMoveOut/hooks.js's
 * useFetchHasOpenCerts (previously unused outside its own file) so it can be
 * shared by the Mini-Profile and any other future consumer.
 *
 * householdId is the applicant-stage twin of residentId: a Pending household
 * (application, no move-in yet) has no Resident row to key the resident-
 * scoped route off of, so it reads the household-scoped route instead. Exactly
 * one of the two is expected to be set by a given caller -- residentId wins
 * if both are (keeps every existing resident-scoped caller's behavior
 * unchanged without an extra prop to thread through).
 */
const useHouseholdOpenCertsQuery = ({
  organizationId,
  propertyId,
  residentId,
  householdId,
  options = {},
}: {
  organizationId: string,
  propertyId: string,
  residentId?: string,
  householdId?: string,
  options?: { enabled?: boolean },
}) => {
  const residentService = useMemo(() => new ResidentService(), []);
  const householdService = useMemo(() => new HouseholdService(), []);
  const hasResidentId = !isEmpty(residentId ?? '');
  const usesHousehold = !hasResidentId && !isEmpty(householdId ?? '');

  return useQuery(
    usesHousehold
      ? ['hasOpenCerts', 'household', organizationId, propertyId, householdId]
      : ['hasOpenCerts', organizationId, propertyId, residentId],
    () =>
      usesHousehold
        ? householdService.getHouseholdHasOpenCerts(
            organizationId,
            propertyId,
            householdId,
          )
        : residentService.getHouseholdHasOpenCerts(
            organizationId,
            propertyId,
            residentId,
          ),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      enabled:
        !isEmpty(organizationId ?? '') &&
        !isEmpty(propertyId ?? '') &&
        (usesHousehold || hasResidentId) &&
        options.enabled !== false,
    },
  );
};

export default useHouseholdOpenCertsQuery;
